import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import {
  addApplication,
  resolveIdentity,
  registerApplication,
  getLevelsOfAssurance,
  addRole,
  getRegistrationProviders,
} from "../../actions";
import { types, successAction, errorAction } from "@authzsvc/api-lib";
import {
  userId,
  isLoading,
  getAllLevelsOfAssurance,
  getAllProviders,
} from "../../selectors";
import AddNewApplicationForm from "../forms/AddNewApplicationForm";
import { Button, Tooltip } from "antd";
import { showErrorNotification } from "../../utils/index";
import * as cfg from "../../config";
import { LevelsOfAssurance } from "../../models/levelsOfAssurance";
import { FullPageLoader } from "@authzsvc/common-react-components";
import { PlusOutlined } from "@ant-design/icons";

const NewApplicationButton = ({
  addApplication,
  registerApplication,
  addRole,
  loading,
  redirect,
  canAdd,
  getLevelsOfAssurance,
  levelsOfAssurance,
  getRegistrationProviders,
  providers,
}) => {
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (getLevelsOfAssurance) getLevelsOfAssurance();
  }, [getLevelsOfAssurance]);

  useEffect(() => {
    if (getRegistrationProviders) getRegistrationProviders();
  }, [getRegistrationProviders]);

  const handleAddApplication = async (
    applicationData,
    providerId,
    registrationsData,
  ) => {
    setIsSubmitting(true);
    const response = await addApplication(applicationData);
    const applicationId = response?.payload?.data?.id;

    try {
      if (response.type === successAction(types.ADD_APPLICATION)) {
        if (providerId !== null) {
          const registrationDetails = {
            ...registrationsData,
            description: applicationData.description,
            name: applicationData.displayName,
          };
          if (
            registrationsData.publicClient != null ||
            registrationsData.definition != null
          ) {
            await handleAddRegistration(
              applicationId,
              providerId,
              registrationDetails,
            );
            setAddModalOpen(false);
            redirect(`${applicationId}/sso`);
          } else {
            setAddModalOpen(false);
            redirect(`${applicationId}`);
          }
        }
      } else if (response.type === errorAction(types.ADD_APPLICATION)) {
        showErrorNotification(
          response.payload?.title,
          response.payload?.data?.message,
        );
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAddRegistration = async (
    applicationId,
    providerId,
    registrationDetails,
  ) => {
    const resp = await registerApplication(
      applicationId,
      providerId,
      registrationDetails,
    );
    if (resp && resp.type === errorAction(types.REGISTER_APPLICATION)) {
      showErrorNotification(resp.payload.title, resp.payload.data.message);
    }
    await handleAddDefaultRole(applicationId);
  };

  const handleAddDefaultRole = async (applicationId) => {
    const defaultLoa = levelsOfAssurance.find(
      (e) => e.value === LevelsOfAssurance.CERN,
    );

    const resp = await addRole({
      minimumLoaId: defaultLoa?.id,
      applyToAllUsers: true,
      name: cfg.default_role_name,
      description: "Users must be from CERN to have access",
      displayName: "Default Allowed Users",
      required: true,
      applicationId,
    });

    if (resp && resp.type === errorAction(types.ADD_ROLE)) {
      showErrorNotification(resp.payload.title, resp.payload.data.message);
    }
  };

  return isSubmitting ? (
    <FullPageLoader />
  ) : !canAdd ? (
    <Tooltip
      placement="left"
      open={!loading}
      title="Applications can only be created by primary or service accounts."
    >
      <span>
        <Button
          id="applicationAddButton"
          type="primary"
          icon={<PlusOutlined />}
          disabled
          style={{ float: "right" }}
        >
          Add an Application
        </Button>
      </span>
    </Tooltip>
  ) : (
    <>
      <Button
        id="applicationAddButton"
        type="primary"
        icon={<PlusOutlined />}
        onClick={() => setAddModalOpen(true)}
        style={{ float: "right" }}
      >
        Add an Application
      </Button>
      <AddNewApplicationForm
        addModalOpen={addModalOpen}
        setAddModalOpen={setAddModalOpen}
        submitFunction={handleAddApplication}
        providers={providers}
      />
    </>
  );
};

NewApplicationButton.propTypes = {
  addApplication: PropTypes.func.isRequired,
  registerApplication: PropTypes.func.isRequired,
  addRole: PropTypes.func.isRequired,
  resolveIdentity: PropTypes.func.isRequired,
  redirect: PropTypes.func.isRequired,
  loading: PropTypes.bool.isRequired,
  canAdd: PropTypes.bool,
  getLevelsOfAssurance: PropTypes.func.isRequired,
  levelsOfAssurance: PropTypes.array.isRequired,
  getRegistrationProviders: PropTypes.func.isRequired,
  providers: PropTypes.array.isRequired,
};

export default connect(
  (state) => ({
    id: userId(state),
    loading: isLoading(state, "resolveIdentity"),
    levelsOfAssurance: getAllLevelsOfAssurance(state),
    providers: getAllProviders(state),
  }),
  {
    addApplication,
    registerApplication,
    addRole,
    resolveIdentity,
    getLevelsOfAssurance,
    getRegistrationProviders,
  },
)(NewApplicationButton);
